// src/providers/i18nProvider.ts
import polyglotI18nProvider from 'ra-i18n-polyglot';
import raEnglishMessages from 'ra-language-english';

// Importar traducciones modulares
import es from '@/i18n/es';
import en from '@/i18n/en';

// Tipo para los idiomas soportados
export type SupportedLocale = 'es' | 'en';

// Mensajes combinados - No tipar estrictamente para evitar conflictos
const messages = {
  en: {
    ...raEnglishMessages,
    ...en,
  },
  es: {
    // Use English as base fallback so all ra.* keys (ra.sort.ASC/DESC, etc.) are available
    ...raEnglishMessages,
    ...es,
  },
};

// Configuración de idiomas disponibles
export const availableLocales = [
  { locale: 'es', name: 'Español' },
  { locale: 'en', name: 'English' },
];

// Idioma por defecto
const defaultLocale: SupportedLocale = 'es';

// Función para obtener el idioma guardado
export const getSavedLocale = (): SupportedLocale => {
  const saved = localStorage.getItem('locale');
  if (saved && (saved === 'es' || saved === 'en')) {
    return saved as SupportedLocale;
  }
  
  // Detectar idioma del navegador
  const browserLocale = navigator.language.split('-')[0];
  if (browserLocale === 'es' || browserLocale === 'en') {
    return browserLocale as SupportedLocale;
  }
  
  return defaultLocale;
};

// Función para guardar el idioma
export const saveLocale = (locale: SupportedLocale): void => {
  localStorage.setItem('locale', locale);
};

// Crear el provider sin tipos estrictos - polyglotI18nProvider maneja los tipos internamente
const i18nProvider = polyglotI18nProvider(
  locale => {
    
    // Guardar el idioma seleccionado si cambió
    if (locale !== localStorage.getItem('locale')) {
      saveLocale(locale as SupportedLocale);
    }
    
    // Retornar los mensajes del idioma o el default
    return (messages as any)[locale] || messages[defaultLocale];
  },
  getSavedLocale(),
  availableLocales
);

// Funciones auxiliares para traducciones

/**
 * Obtiene una traducción específica
 */
export const getTranslation = (key: string, locale?: SupportedLocale): string => {
  const currentLocale = locale || getSavedLocale();
  const messageObj = messages[currentLocale];
  
  // Navegar por el objeto de mensajes usando la key con puntos
  const keys = key.split('.');
  let result: any = messageObj;
  
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      // Si no se encuentra, intentar con el idioma por defecto
      if (currentLocale !== defaultLocale) {
        return getTranslation(key, defaultLocale);
      }
      return key; // Devolver la key si no se encuentra traducción
    }
  }
  
  return typeof result === 'string' ? result : key;
};

/**
 * Formatea una traducción con parámetros
 */
export const formatTranslation = (
  key: string,
  params: Record<string, any>,
  locale?: SupportedLocale
): string => {
  let translation = getTranslation(key, locale);
  
  // Reemplazar parámetros en el formato %{param}
  Object.entries(params).forEach(([param, value]) => {
    translation = translation.replace(`%{${param}}`, String(value));
  });
  
  return translation;
};

/**
 * Obtiene traducciones para un recurso específico
 */
export const getResourceTranslations = (
  resource: string,
  locale?: SupportedLocale
): Record<string, any> => {
  const currentLocale = locale || getSavedLocale();
  const messageObj = messages[currentLocale];
  
  if (messageObj.resources && messageObj.resources[resource]) {
    return messageObj.resources[resource];
  }
  
  // Si no se encuentra, intentar con el idioma por defecto
  if (currentLocale !== defaultLocale) {
    return getResourceTranslations(resource, defaultLocale);
  }
  
  return {};
};

/**
 * Hook personalizado para cambiar idioma
 */
export const useChangeLocale = () => {
  const changeLocale = (locale: SupportedLocale) => {
    saveLocale(locale);
    // Recargar la página para aplicar el cambio
    // React Admin maneja esto internamente, pero a veces es necesario
    window.location.reload();
  };
  
  return changeLocale;
};

// Tipos para las traducciones (útil para autocompletado)
export interface TranslationKeys {
  // Common
  'common.actions.save': string;
  'common.actions.cancel': string;
  'common.actions.delete': string;
  'common.actions.edit': string;
  'common.actions.create': string;
  
  // Resources
  'resources.lockers.name': string;
  'resources.lockers.fields.displayNumber': string;
  'resources.lockers.fields.status': string;
  
  'resources.users.name': string;
  'resources.users.fields.name': string;
  'resources.users.fields.email': string;
  'resources.users.fields.role': string;
  
  // Add more as needed...
}

export default i18nProvider;